/**
 * What each role is for, in words a person choosing one can act on.
 *
 * The capability *sets* live on the server and arrive with the session — this
 * file never decides what anybody may do. What it holds is the explanation: a
 * dropdown listing "developer, marketing, support" and nothing else asks the
 * person picking to already know the answer, which is the thing every product
 * with roles gets wrong.
 *
 * `summary` is the one-line version shown beside the name. `can` and `cannot`
 * are the two or three points that actually decide the choice — deliberately not
 * the whole capability list, which is twenty-odd lines and reads as noise at the
 * moment somebody is trying to pick.
 */

import type { Role } from "./api/types";

export interface RoleInfo {
  role: Role;
  label: string;
  summary: string;
  can: string[];
  cannot: string[];
}

/**
 * Ordered widest first, which is how the picker reads.
 *
 * Not an ordering in the permission sense — developer and marketing are
 * genuinely incomparable, and neither is "below" the other. It is a reading
 * order, and the summaries say what actually separates them.
 */
export const ROLES: RoleInfo[] = [
  {
    role: "owner",
    label: "Owner",
    summary: "Full control, including deleting the organization.",
    can: ["Everything an admin can", "Transfer ownership", "Delete the organization"],
    cannot: [],
  },
  {
    role: "admin",
    label: "Admin",
    summary: "Runs the organization. Everything except ending it.",
    can: ["Invite and remove people", "Manage teams, keys, domains", "Erase contacts"],
    cannot: ["Delete or transfer the organization"],
  },
  {
    role: "developer",
    label: "Developer",
    summary: "Wires the integration. Sends transactional mail, never campaigns.",
    can: ["Issue API keys", "Manage webhooks and sending domains", "Send transactional mail"],
    cannot: ["Send campaigns", "Edit contacts", "Invite people"],
  },
  {
    role: "marketing",
    label: "Marketing",
    summary: "Owns the audience and the campaigns sent to it.",
    can: ["Send campaigns", "Manage contacts, groups and templates", "Import and export the audience"],
    cannot: ["Issue API keys", "Manage sending domains", "Invite people"],
  },
  {
    role: "support",
    label: "Support",
    summary: "Answers “did my email arrive?”. Reads messages, resends one.",
    can: ["Read message contents", "Resend a message", "Add and remove suppressions"],
    cannot: ["Send campaigns", "Export the audience", "Edit contacts"],
  },
  {
    role: "analyst",
    label: "Analyst",
    summary: "Reads everything and sends nothing.",
    can: ["Read every screen", "Export contacts for analysis"],
    cannot: ["Send anything", "Read message contents", "Change anything"],
  },
  {
    role: "viewer",
    label: "Viewer",
    summary: "Read-only, without message contents or exports.",
    can: ["Read the message log, contacts and analytics"],
    cannot: ["Send anything", "Read message contents", "Export contacts"],
  },
];

const byRole = new Map(ROLES.map((r) => [r.role, r]));

/**
 * Look a role up, falling back to something readable.
 *
 * The fallback matters: the server's enum is the authority, and a role added
 * there before this file catches up should render as its own name rather than
 * as blank space or a crash.
 */
export function roleInfo(role: Role): RoleInfo {
  return (
    byRole.get(role) ?? {
      role,
      label: role,
      summary: "",
      can: [],
      cannot: [],
    }
  );
}

export function roleLabel(role: Role): string {
  return roleInfo(role).label;
}

/**
 * The roles somebody may hand out.
 *
 * Mirrors the server's rule — nobody grants what they do not hold — so the
 * picker does not offer a choice the request will refuse. The server checks
 * again; this only keeps the UI honest.
 *
 * Owner is offered to owners alone. Everything else is offered to anyone who
 * can manage members, because `members:manage` belongs to owner and admin only
 * and both hold every non-owner capability.
 */
export function grantableRoles(granterRole: Role | null): RoleInfo[] {
  if (granterRole === "owner") return ROLES;
  if (granterRole === "admin") return ROLES.filter((r) => r.role !== "owner");
  return [];
}

/**
 * The capability sets an API key is usually issued with.
 *
 * Presets rather than a checklist of twenty-six. Somebody minting a key for a
 * webhook consumer knows what the consumer does, not which of `messages:read`
 * and `messages:read_content` it needs — and a wall of checkboxes is answered by
 * ticking everything, which is the outcome the whole feature exists to avoid.
 *
 * "Full access" is deliberately first and deliberately not the default: it is
 * what every key did before capabilities existed, so it has to remain
 * available, but the narrower ones should be what the eye lands on.
 */
export interface KeyPreset {
  id: string;
  label: string;
  summary: string;
  /** Undefined means "everything this key's shape implies" — the old behaviour. */
  capabilities?: string[];
}

export const KEY_PRESETS: KeyPreset[] = [
  {
    id: "read-only",
    label: "Read-only",
    summary: "Read the message log and analytics. Sends nothing, changes nothing.",
    capabilities: ["messages:read", "analytics:read"],
  },
  {
    id: "transactional",
    label: "Transactional sending",
    summary: "Send mail and read what happened to it. The usual application key.",
    // Deliberately without templates:read. An application sending its own
    // rendered mail has no reason to enumerate the marketing content, and this
    // is the preset most keys end up with — so it is the one worth keeping
    // tight.
    capabilities: ["messages:send", "messages:read", "messages:read_content"],
  },
  {
    id: "template-sending",
    label: "Sending with templates",
    summary: "Send mail using stored templates, and read them to do it.",
    capabilities: ["messages:send", "messages:read", "messages:read_content", "templates:read"],
  },
  {
    id: "audience-sync",
    label: "Audience sync",
    summary: "Create and update contacts from another system. Cannot send.",
    capabilities: ["contacts:read", "contacts:write", "contacts:import"],
  },
  {
    id: "full",
    label: "Full access",
    summary: "Everything the key's scope allows. What keys did before capabilities existed.",
  },
];

/**
 * What a person is being told when a 403 names a capability.
 *
 * The server puts `required_capability` on every capability refusal and the
 * dashboard read it nowhere — `grep -rn required_capability web/src` returned
 * zero hits, and `Problem` did not declare the field.
 *
 * WHAT THAT ACTUALLY COST WAS LESS THAN IT LOOKED, and measuring it is what
 * said so. The server's own detail is already a sentence aimed at a person:
 * "This credential does not have the campaigns:write capability. Ask an owner
 * or admin of this organization for a role that includes it." So the refusal
 * was never the bare "Forbidden" the field was introduced to improve on, and a
 * plain-language replacement that dropped the second half would have been a
 * downgrade wearing a friendlier voice.
 *
 * What is missing from it is only the first thing a person wants: *what* they
 * were stopped from doing, in their words rather than as an identifier. So this
 * prepends that and keeps everything the server said. "Ask an owner or admin"
 * is the actionable half, and `campaigns:write` is what that administrator
 * searches for — losing either makes the message pleasanter and less useful.
 *
 * WHAT THIS FILE MAY AND MAY NOT DO is unchanged by adding it. The capability
 * sets live on the server; nothing here decides anything. A wrong entry below
 * produces a badly worded refusal, never a wrongly permitted or wrongly refused
 * request — which is what makes it safe to keep the wording in the browser.
 */
const CAPABILITY_REFUSALS: Record<string, string> = {
  "messages:send": "send mail",
  "messages:read": "see the message log",
  "messages:read_content": "read message bodies",
  "contacts:read": "see contacts",
  "contacts:write": "add or change contacts",
  "contacts:import": "import contacts",
  "contacts:export": "export contacts",
  "contacts:erase": "erase a contact's personal data",
  "custom_fields:read": "see custom fields",
  "custom_fields:manage": "add or remove custom fields",
  "templates:read": "see templates",
  "templates:write": "create or edit templates",
  "templates:publish": "publish a template version",
  "campaigns:read": "see campaigns",
  "campaigns:write": "create or edit campaigns",
  "campaigns:send": "send campaigns",
  "forms:read": "see subscription forms",
  "forms:manage": "create or change subscription forms",
  "domains:read": "see sending domains",
  "domains:manage": "add or verify sending domains",
  "suppressions:read": "see blocked addresses",
  "suppressions:add": "block an address",
  "suppressions:remove": "unblock an address",
  "sending:resume": "resume sending after it is paused",
  "keys:manage": "create or revoke API keys",
  "webhooks:manage": "configure webhooks",
  "analytics:read": "see analytics",
  "usage:read": "see this organization's usage",
  "members:manage": "add, remove or change members",
  "teams:read": "see teams",
  "teams:manage": "create or change teams",
  "org:manage": "change organization settings",
  "org:transfer": "transfer ownership",
  "org:delete": "delete the organization",
  "audit:read": "read the audit log",
};

/**
 * Prefixes the server's refusal with what the person was actually stopped from
 * doing. Null for a capability this file does not know.
 *
 * Null rather than a generic fallback, and additive rather than replacing.
 * A capability added on the server and missing here would otherwise render as
 * "You cannot do that", which is strictly worse than the detail the server
 * already writes — so an unknown capability falls through to that untouched.
 * Same rule ROLES follows for a role it does not recognise.
 */
export function explainRefusal(capability: string, serverDetail?: string): string | null {
  const what = CAPABILITY_REFUSALS[capability];
  if (!what) return null;

  const lead = `Your account cannot ${what}.`;
  return serverDetail ? `${lead} ${serverDetail}` : lead;
}
